import { Component, inject, Input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { SearchStateService } from '../../shared/services/search-state.service';
import { CommonModule } from '@angular/common';
import { Observable, of } from 'rxjs';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { PostGetLocationSearchRequest } from '../../../shared/model/providerlocationsearch/PostGetProviderSearchRequest';
import { MCAppGatewayService } from '../../../shared/services/mcappgateway/mcappgateway.service';
import { TranslateService } from '@ngx-translate/core';
import { LocationSearchFilters, SearchFilter } from '../../../shared/model/providerlocationsearch/GetLocationSearchFilterResponse';

@Component({
  selector: 'app-location-search-filter',
  imports: [CommonModule, FormsModule, MatAutocompleteModule, MatCheckboxModule, MatChipsModule, MatIconModule],
  templateUrl: './location-search-filter.component.html',
  styleUrl: './location-search-filter.component.css'
})

export class LocationSearchFilterComponent {
  @Input() currentLocation: { latitude: number; longitude: number } | null = null;

  searchState = inject(SearchStateService);
  mcAppGateway = inject(MCAppGatewayService);

  locationSearchQuery$ = this.searchState.locationSearchQuery$;
  locationSearchResults$ = this.searchState.locationSearchResults$;
  locationSearchFilters$ = this.searchState.locationSearchFilters$;
  locationSortMethod$ = this.searchState.locationSortMethod$;
  currentFilters: LocationSearchFilters | null = null;

  locationSearchResults: any[] = [];
  locationSortMethod:'distance' | 'availability' | 'name' = 'availability';

  filteredServices$!: Observable<SearchFilter[]>;
  serviceSearchText: string = '';
  allServices: SearchFilter[] = [];
  selectedServices: SearchFilter[] = [];

  filteredTypes$!: Observable<SearchFilter[]>;
  typeSearchText: string = '';
  allTypes: SearchFilter[] = [];
  selectedTypes: SearchFilter[] = [];

  queryLocationName: string = '';
  queryCityOrZip: string = '';

  serviceCount: number = 0;
  typeCount: number = 0;

  locale = "en-US";
  searchPlaceholderText = "Search";
  locationLabel = "Location Name";
  serviceLabel = "Service";
  typeLabel = "Type";
  clearBtnText = "Clear all";
  applyBtnText = "Apply";

  showSpinner: boolean = false;

  constructor(private translate: TranslateService) {
    this.locale =  sessionStorage.getItem('locale') || 'en-US';
    this.translate.use(this.locale); // Default to 'en-US' if no locale is provided
  }

  ngOnInit(): void {

    //translations
    this.translate.get('SEARCH').subscribe((translated: string) => {
      this.searchPlaceholderText = translated;
    });
    
    this.translate.get('FILTERS.LOCATION_NAME').subscribe((translated: string) => {
      this.locationLabel = translated;
    });

    this.translate.get('FILTERS.LOCATION_SERVICE').subscribe((translated: string) => {
      this.serviceLabel = translated;
    });

    this.translate.get('FILTERS.LOCATION_TYPE').subscribe((translated: string) => {
      this.typeLabel = translated;
    });

    this.translate.get('APPLY').subscribe((translated: string) => {
      this.applyBtnText = translated;
    });

    this.translate.get('CLEAR_ALL').subscribe((translated: string) => {
      this.clearBtnText = translated;
    });
    
    this.locationSearchQuery$.subscribe(query => {
      if (query) {
        this.queryLocationName = query.facilityOrLocationName; // LocationName "search results" on top of screen
        this.queryCityOrZip = query.cityOrZip; // update the city/zip filter input value 
      }
    });

    this.locationSearchResults$.subscribe(results => {
      this.locationSearchResults = results ?? [];
    });

    this.locationSortMethod$.subscribe(sortBy => {
      this.locationSortMethod = sortBy;
    });

    this.locationSearchFilters$.subscribe((filters: LocationSearchFilters | null) => {
      console.log('Filters in Component:', filters);
      this.currentFilters = filters;

      // Calculate service count and initialize service list
      if (filters) {
        if (filters.serviceList) {
          this.allServices = filters.serviceList;
          this.selectedServices = filters.serviceList.filter(option => option.isSelected == true);
          this.serviceCount = this.selectedServices.length;
          // Initialize with unselected services only
          this.filteredServices$ = of(this.allServices.filter(s => !s.isSelected));
        } 

        if (filters.typeList) {
          this.allTypes = filters.typeList;
          this.selectedTypes = filters.typeList.filter(option => option.isSelected == true);
          this.typeCount = this.selectedTypes.length;
          // Initialize with unselected services only
          this.filteredTypes$ = of(this.allTypes.filter(s => !s.isSelected));
        } 
      }
    });  
  }

  filterServices(searchText: string): void {
    if (!searchText || searchText.trim() === '') {
      // Filter out already selected services
      this.filteredServices$ = of(this.allServices.filter(s => !s.isSelected));
    } else {
      const filterValue = searchText.toLowerCase();
      // Filter by search text AND exclude already selected services
      const filtered = this.allServices.filter(service => 
        service.value.toLowerCase().includes(filterValue) && !service.isSelected
      );
      this.filteredServices$ = of(filtered);
    }
  }

  onServiceSelected(event: MatAutocompleteSelectedEvent): void {
    const selectedValue = event.option.value;
    const service = this.allServices.find(s => s.value === selectedValue);
    
    if (service && !service.isSelected) {
      service.isSelected = true;
      this.selectedServices = this.allServices.filter(s => s.isSelected);
      this.serviceCount = this.selectedServices.length;
      console.log('Service added:', service);
      console.log('COUNT:', this.serviceCount);
      console.log('Current selected services:', this.selectedServices);
      console.log('Current Filters:', this.currentFilters);
    }
    
    // Clear the search text after selection
    // use setTimeout for Material Autocomplete
    setTimeout(() => {
      this.serviceSearchText = '';
      // Filter out selected services from the list
      this.filteredServices$ = of(this.allServices.filter(s => !s.isSelected));
    }, 0);
  }

  removeService(service: SearchFilter): void {
    service.isSelected = false;
    this.selectedServices = this.allServices.filter(s => s.isSelected);
    this.serviceCount = this.selectedServices.length;
    
    // Refresh the filtered list to include the removed service
    this.filteredServices$ = of(this.allServices.filter(s => !s.isSelected));
    
    console.log('Service removed:', service);
    console.log('COUNT:', this.serviceCount);
    console.log('Current selected services:', this.selectedServices);
    console.log('Current Filters:', this.currentFilters);
  }

  filterTypes(searchText: string): void {
    if (!searchText || searchText.trim() === '') {
      // Filter out already selected types
      this.filteredTypes$ = of(this.allTypes.filter(t => !t.isSelected));
    } else {
      const filterValue = searchText.toLowerCase();
      // Filter by search text AND exclude already selected types
      const filtered = this.allTypes.filter(type => 
        type.value.toLowerCase().includes(filterValue) && !type.isSelected
      );
      this.filteredTypes$ = of(filtered);
    }
  }

  onTypeSelected(event: MatAutocompleteSelectedEvent): void {
    const selectedValue = event.option.value;
    const type = this.allTypes.find(t => t.value === selectedValue);
    
    if (type && !type.isSelected) {
      type.isSelected = true;
      this.selectedTypes = this.allTypes.filter(t => t.isSelected);
      this.typeCount = this.selectedTypes.length;
      console.log('Type added:', type);
      console.log('COUNT:', this.typeCount);
      console.log('Current selected types:', this.selectedTypes);
      console.log('Current Filters:', this.currentFilters);
    }
    
    // Clear the search text after selection
    // use setTimeout for Material Autocomplete
    setTimeout(() => {
      this.typeSearchText = '';
      // Filter out selected types from the list
      this.filteredTypes$ = of(this.allTypes.filter(t => !t.isSelected));
    }, 0);
  }

  removeType(type: SearchFilter): void {
    type.isSelected = false;
    this.selectedTypes = this.allTypes.filter(t => t.isSelected);
    this.typeCount = this.selectedTypes.length;
    
    // Refresh the filtered list to include the removed type
    this.filteredTypes$ = of(this.allTypes.filter(t => !t.isSelected));
    
    console.log('Type removed:', type);
    console.log('COUNT:', this.typeCount);
    console.log('Current selected types:', this.selectedTypes);
    console.log('Current Filters:', this.currentFilters);
  }

  onFilterChange(filterType: string, option: any) {
    console.log(`${filterType} filter changed (not applied yet):`, option);
    // console.log('Current Filters:', this.currentFilters);    
  }

  onApplyFilters() {
    console.log('Applying Filters:', this.currentFilters);

    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.searchState.clearCurrentLocationDetailsProvider();
    this.searchState.clearCurrentLocationDetails();
    this.searchState.clearCurrentProviderDetailsLocation();
    this.searchState.clearCurrentProviderDetails();
    this.searchState.setShowSpinner(true);

    var request = new PostGetLocationSearchRequest();

    request.locationName = this.queryLocationName; // sourced from the input text field
    request.cityOrZip = this.queryCityOrZip; // sourced from the LocationResults component via stateService
    request.distanceInMiles = this.currentFilters?.distanceInMiles ?? 15;

    request.services = this.allServices?.filter(s => s.isSelected).map(s => s.value).join(', ');
    request.types = this.allTypes?.filter(t => t.isSelected).map(t => t.value).join(', ');

    // only set the latitude and longitude if a cityOrZip value is not provided
    if ((request.cityOrZip == null || request.cityOrZip == "") && this.currentLocation != null && this.currentLocation.latitude && this.currentLocation.longitude ) {
      request.latitude = this.currentLocation.latitude;
      request.longitude = this.currentLocation.longitude;
    }
    
    this.mcAppGateway.PostGetLocationSearch(request).subscribe({
      next: (response) => {
        var sortedResponse = this.searchState.sortLocationSearchResults(response || [], this.locationSortMethod);
        console.log('Location Search Response (after applying filters):', sortedResponse);
        this.searchState.setLocationSearchResults(sortedResponse);
        this.searchState.setLocationSearchFilters(this.currentFilters!);
        this.searchState.setLocationSearchQuery({
          facilityOrLocationName: this.queryLocationName, // update display " '<query>' search results" on top of screen
          cityOrZip: this.queryCityOrZip,
        });

        this.searchState.setShowSpinner(false);
      },
      error: (error) => {
        console.error('An error occurred while searching for locations with filters: ', error);
        alert('An error occurred while searching for locations with filters: ' + error.message);

        this.searchState.setShowSpinner(false);
      },
    });

    // set the results based on the api response
    // set the location filters here as well
    // this.searchState.setLocationSearchResults(response);
    this.searchState.setLocationSearchFilters(this.currentFilters!);
  }

  onClearAllFilters(): void {
    // Clear location name text
    this.queryLocationName = '';
    
    // Clear service search text
    this.serviceSearchText = '';
    
    // Unselect all services
    if (this.allServices && this.allServices.length > 0) {
      this.allServices.forEach(service => {
        service.isSelected = false;
      });
    }
    this.selectedServices = [];
    this.serviceCount = 0;
    
    // Reset filtered services list
    this.filteredServices$ = of(this.allServices);
    
    // Clear type search text
    this.typeSearchText = '';
    
    // Unselect all types
    if (this.allTypes && this.allTypes.length > 0) {
      this.allTypes.forEach(type => {
        type.isSelected = false;
      });
    }
    this.selectedTypes = [];
    this.typeCount = 0;
    
    // Reset filtered types list
    this.filteredTypes$ = of(this.allTypes);
    
    console.log('All filters cleared');
  }
}